// Generated by GitHub Copilot
import React, { useState } from "react";
import { Form, Badge, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";
import { FaInfoCircle, FaExclamationTriangle, FaFlask } from "react-icons/fa";
import { capitalize } from "../../utils/textFormatters";
import styles from "./SelfServiceFeatureToggle.module.css";

/**
 * SelfServiceFeatureToggle Component
 *
 * Interactive toggle for individual feature flags in self-service mode.
 * Based on existing FeatureToggle.js but optimized for self-service use.
 */
const SelfServiceFeatureToggle = ({
  feature,
  enabled,
  onChange,
  disabled = false,
  loading = false,
}) => {
  const [isToggling, setIsToggling] = useState(false);

  /**
   * Get feature icon based on type
   */
  const getFeatureIcon = () => {
    if (feature.experimental) {
      return <FaFlask className="text-warning" />;
    }
    if (feature.beta) {
      return <FaInfoCircle className="text-info" />;
    }
    return null;
  };

  /**
   * Handle toggle change with loading state
   */
  const handleChange = async (event) => {
    const newValue = event.target.checked;

    if (disabled || loading || isToggling) {
      return;
    }

    setIsToggling(true);

    try {
      await onChange(feature.name, newValue);
    } catch (error) {
      console.error("Error toggling feature:", error);
      // The onChange function should handle the error display
    } finally {
      setIsToggling(false);
    }
  };

  /**
   * Create detailed tooltip with feature information
   */
  const tooltip = (
    <Tooltip>
      <div>
        <strong>{feature.displayName}</strong>
        <br />
        {feature.description}
        {feature.experimental && (
          <>
            <br />
            <small className="text-warning">
              ⚠️ Experimental feature - use with caution
            </small>
          </>
        )}
        {feature.beta && (
          <>
            <br />
            <small className="text-info">
              ℹ️ Beta feature - may have limited functionality
            </small>
          </>
        )}
        {feature.dependencies && feature.dependencies.length > 0 && (
          <>
            <br />
            <small>Dependencies: {feature.dependencies.join(", ")}</small>
          </>
        )}
      </div>
    </Tooltip>
  );

  const isCurrentlyDisabled = disabled || loading || isToggling;

  return (
    <div className="feature-toggle-item d-flex align-items-center justify-content-between mb-3 p-3 border rounded">
      <div className="d-flex align-items-center flex-grow-1">
        {/* Toggle Switch */}
        <Form.Check
          type="switch"
          id={`self-service-feature-${feature.name}`}
          checked={enabled}
          onChange={handleChange}
          disabled={isCurrentlyDisabled}
          className="me-3"
          aria-label={`Toggle ${feature.displayName}`}
          data-testid={`feature-toggle-${feature.name}`}
        />

        {/* Feature Information */}
        <div className="flex-grow-1">
          <div className="d-flex align-items-center mb-1">
            <h6 className="mb-0 me-2">{feature.displayName}</h6>

            {/* Feature Type Badges */}
            {(feature.experimental || feature.beta) && (
              <Badge
                className={`${styles.featureBadge} ${feature.experimental ? styles.experimentalBadge : styles.betaBadge} me-2`}
              >
                {getFeatureIcon()}
                {feature.experimental ? "Experimental" : "Beta"}
              </Badge>
            )}

            {/* Category Badge */}
            <Badge className={`${styles.featureBadge} ${styles.categoryBadge} me-2`}>
              {capitalize(feature.category)}
            </Badge>

            {/* Info Icon with Tooltip */}
            <OverlayTrigger placement="top" overlay={tooltip}>
              <span
                className="d-inline-flex align-items-center"
                style={{ cursor: "help" }}
                aria-label="Feature information"
              >
                <FaInfoCircle className="text-muted" />
              </span>
            </OverlayTrigger>
          </div>

          {/* Feature Description */}
          <p className="text-muted mb-1 small">{feature.description}</p>

          {/* Dependencies Warning */}
          {feature.dependencies && feature.dependencies.length > 0 && (
            <div className="mb-1">
              <small className="text-warning">
                <FaExclamationTriangle className="me-1" />
                Requires: {feature.dependencies.join(", ")}
              </small>
            </div>
          )}

          {/* Experimental/Beta Warnings */}
          {feature.experimental && (
            <div>
              <small className="text-warning">
                <FaExclamationTriangle className="me-1" />
                Experimental feature - behavior may change
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Status and Loading Indicator */}
      <div className="text-center feature-flag-status">
        {isToggling ? (
          <div className="d-flex align-items-center">
            <Spinner size="sm" className="me-2" />
            <span className="small text-muted">Updating...</span>
          </div>
        ) : (
          <div>
            <Badge className={`${enabled ? styles.statusEnabledBadge : styles.statusDisabledBadge} mb-1`}>
              {enabled ? "Enabled" : "Disabled"}
            </Badge>
            {isCurrentlyDisabled && !isToggling && (
              <div>
                <small className="text-muted">
                  {disabled ? "Restricted" : "Loading..."}
                </small>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfServiceFeatureToggle;
